import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import MenuOverlay from '../components/MenuOverlay';
import useSlideshows from '../hooks/useSlideshow';
import PROJECTS from '../data/projects';

// ── Presentational components ────────────────────────────────────────

const SlideshowBackground = ({ images, bgIndex, prevIndex, slideDirection }) => (
  <>
    {images.map((img, imgIndex) => {
      const isCurrent = imgIndex === bgIndex;
      const wasShowing = imgIndex === prevIndex;

      let translateX = '0%';
      let opacity = 0;

      if (isCurrent) {
        translateX = '0%';
        opacity = 1;
      } else if (wasShowing) {
        translateX = slideDirection === 'forward' ? '-100%' : '100%';
        opacity = 0;
      } else {
        if (slideDirection === 'forward') {
          translateX = imgIndex > bgIndex ? '100%' : '-100%';
        } else {
          translateX = imgIndex < bgIndex ? '-100%' : '100%';
        }
        opacity = 0;
      }

      return (
        <div
          key={imgIndex}
          className="absolute top-0 left-0 w-full h-full transition-all duration-[800ms] ease-in-out"
          style={{
            backgroundImage: `url(${img})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateX(${translateX})`,
            opacity,
          }}
        />
      );
    })}
  </>
);

const SlideContent = ({
  slide,
  slideshow,
  images,
  isMobile,
  isActive,
  isFirst,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
}) => (
  <section
    className={`${isFirst ? 'relative' : 'sticky top-0'} h-screen w-full`}
    onTouchStart={isMobile ? onTouchStart : undefined}
    onTouchMove={isMobile ? onTouchMove : undefined}
    onTouchEnd={
      isMobile
        ? (e) => onTouchEnd(e, slideshow.goNext, slideshow.goPrev)
        : undefined
    }
    style={{ touchAction: isMobile ? 'pan-y' : undefined }}
  >
    {/* Title + Play button */}
    <div className="relative z-10 flex items-center justify-center md:justify-end w-full h-full px-4 md:pr-24 lg:pr-32">
      <div
        className="flex flex-col items-center md:items-end"
        style={{ marginLeft: isMobile ? '0' : '-350px' }}
      >
        <h2
          className="text-white text-2xl md:text-6xl lg:text-7xl font-bold uppercase tracking-wider text-center md:text-right"
          style={{
            fontFamily: 'Montserrat, sans-serif',
            textShadow: '0 4px 8px rgba(0,0,0,0.5)',
          }}
        >
          {slide.title}
        </h2>
        <Link
          to={`/project/${slide.id}`}
          className="mt-4 px-3 py-0.5 text-white uppercase text-base md:text-lg font-bold tracking-wider transition-colors duration-300"
          style={{
            fontFamily: 'Montserrat, sans-serif',
            backgroundColor: '#36454F',
            textDecoration: 'none',
            lineHeight: 1.2,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#ffffff';
            e.currentTarget.style.color = '#000000';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#36454F';
            e.currentTarget.style.color = '#ffffff';
          }}
        >
          PLAY
        </Link>
      </div>
    </div>

    {/* Left Arrow */}
    <div
      onClick={slideshow.goPrev}
      className="arrow arrow-left cursor-pointer"
      style={{
        position: 'absolute',
        left: isMobile ? '8px' : '40px',
        top: '50%',
        zIndex: 15,
        padding: isMobile ? '20px' : '0',
        transform: isMobile
          ? 'translateY(-50%) rotate(-180deg) scale(0.7)'
          : undefined,
      }}
      role="button"
      aria-label="Previous image"
    >
      <div className="arrow-top"></div>
      <div className="arrow-bottom"></div>
    </div>

    {/* Right Arrow */}
    <div
      onClick={slideshow.goNext}
      className="arrow arrow-right cursor-pointer"
      style={{
        position: 'absolute',
        right: isMobile ? '8px' : '40px',
        left: 'auto',
        top: '50%',
        zIndex: 15,
        padding: isMobile ? '20px' : '0',
        transform: isMobile ? 'translateY(-50%) scale(0.7)' : undefined,
      }}
      role="button"
      aria-label="Next image"
    >
      <div className="arrow-top"></div>
      <div className="arrow-bottom"></div>
    </div>

    {/* Dot indicators — only for the currently visible slide */}
    {isActive && (
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          zIndex: 20,
        }}
      >
        {images.map((_, idx) => (
          <div
            key={idx}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor:
                idx === slideshow.bgIndex ? '#fff' : 'rgba(255,255,255,0.4)',
              transition: 'background-color 0.3s',
            }}
          />
        ))}
      </div>
    )}
  </section>
);

// ── Main component ───────────────────────────────────────────────────

const MyWork = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Resolve image sets per slide
  const resolvedImages = PROJECTS.map((s) =>
    isMobile ? s.mobileImages : s.images
  );

  // Single hook call manages all slideshows — no code changes needed when adding projects
  const slideshows = useSlideshows(resolvedImages);

  // Touch / swipe state
  const [touchStart, setTouchStart] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
    setTouchStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (!touchStart || !touchStartY) return;
    const diffX = Math.abs(touchStart - e.touches[0].clientX);
    const diffY = Math.abs(touchStartY - e.touches[0].clientY);
    if (diffX > diffY && diffX > 10) e.preventDefault();
  };

  const handleTouchEnd = (e, goNext, goPrev) => {
    if (!touchStart) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (diff > 50) goNext();
    else if (diff < -50) goPrev();
    setTouchStart(null);
    setTouchStartY(null);
  };

  // Scroll management + snap
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');

    if (!isMenuOpen) {
      html.style.overflow = 'auto';
      html.style.height = 'auto';
      body.style.overflow = 'auto';
      body.style.height = 'auto';
      body.style.position = 'static';
      if (root) {
        root.style.overflow = 'auto';
        root.style.height = 'auto';
        root.style.position = 'static';
      }
    } else {
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      if (root) root.style.overflow = 'hidden';
    }

    let scrollTimeout;
    let isSnapping = false;

    const handleScroll = () => {
      setScrollY(window.scrollY);

      if (isSnapping || isMenuOpen) return;

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const sections = document.querySelectorAll('section');
        let closestSection = null;
        let minDistance = Infinity;

        sections.forEach((section) => {
          const distance = Math.abs(section.getBoundingClientRect().top);
          if (distance < minDistance) {
            minDistance = distance;
            closestSection = section;
          }
        });

        if (closestSection && minDistance > 10) {
          isSnapping = true;
          window.scrollTo({
            top: closestSection.offsetTop,
            behavior: 'smooth',
          });
          setTimeout(() => {
            isSnapping = false;
          }, 600);
        }
      }, 150);
    };

    if (!isMenuOpen) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      setScrollY(window.scrollY);
    }

    return () => {
      html.style.overflow = 'hidden';
      html.style.height = '100%';
      body.style.overflow = 'hidden';
      body.style.height = '100%';
      body.style.position = 'fixed';
      if (root) {
        root.style.overflow = 'hidden';
        root.style.height = '100%';
        root.style.position = 'fixed';
      }
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [isMenuOpen]);

  // Derived values for N-slide layering
  const slideCount = PROJECTS.length;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 1;
  const activeSlideIndex = Math.min(
    slideCount - 1,
    Math.floor(scrollY / vh + 0.5)
  );

  const handleMenuOpen = () => setIsMenuOpen(true);
  const handleMenuClose = () => setIsMenuOpen(false);
  const handleHomeClick = () => navigate('/');

  return (
    <>
      {/* Global Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-20 p-8 md:p-10 lg:p-10">
        <div className="flex items-center justify-between w-full">
          <div className="w-10 md:w-20"></div>
          <div className="flex-1 flex justify-center">
            <h1
              onClick={handleHomeClick}
              className="text-white text-lg md:text-xl lg:text-2xl font-light tracking-widest uppercase cursor-pointer"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              Joshua Gavin
            </h1>
          </div>
          <button
            onClick={handleMenuOpen}
            className="menu-button-work"
            style={{
              width: isMobile ? '40px' : '80px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: isMobile ? '6px' : '8px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              margin: 0,
            }}
            aria-label="Open menu"
          >
            <span
              className="menu-bar-work"
              style={{
                width: isMobile ? '24px' : '32px',
                height: '2px',
                backgroundColor: '#ffffff',
                display: 'block',
                transition: 'width 0.3s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}
            ></span>
            <span
              className="menu-bar-work"
              style={{
                width: isMobile ? '24px' : '32px',
                height: '2px',
                backgroundColor: '#ffffff',
                display: 'block',
                transition: 'width 0.3s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}
            ></span>
            {isMobile && (
              <span
                className="menu-bar-work"
                style={{
                  width: '24px',
                  height: '2px',
                  backgroundColor: '#ffffff',
                  display: 'block',
                  transition: 'width 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                }}
              ></span>
            )}
          </button>
        </div>
        <style>{`
          .menu-button-work:hover .menu-bar-work {
            width: 48px !important;
          }
        `}</style>
      </nav>

      {/* Fixed background layers — bottom slide rendered first, top slide last */}
      {PROJECTS.map((slide, i) => {
        const images = resolvedImages[i];
        const zIndex = slideCount - 1 - i;
        const isLastSlide = i === slideCount - 1;
        const clipPercent = isLastSlide
          ? 0
          : Math.min(100, Math.max(0, ((scrollY - i * vh) / vh) * 100));
        const clipStyle = isLastSlide
          ? {}
          : { clipPath: `inset(0 0 ${clipPercent}% 0)` };

        return (
          <div
            key={slide.id}
            className="fixed inset-0 bg-black overflow-hidden"
            style={{ zIndex, ...clipStyle }}
          >
            <SlideshowBackground
              images={images}
              bgIndex={slideshows[i].bgIndex}
              prevIndex={slideshows[i].prevIndex}
              slideDirection={slideshows[i].slideDirection}
            />
          </div>
        );
      })}

      {/* Scrolling content */}
      <main
        className="relative z-10"
        style={{ minHeight: `${slideCount * 100}vh` }}
      >
        {PROJECTS.map((slide, i) => (
          <SlideContent
            key={slide.id}
            slide={slide}
            slideshow={slideshows[i]}
            images={resolvedImages[i]}
            isMobile={isMobile}
            isActive={i === activeSlideIndex}
            isFirst={i === 0}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          />
        ))}
      </main>

      <MenuOverlay isOpen={isMenuOpen} onClose={handleMenuClose} />
    </>
  );
};

export default MyWork;
